import { useCallback, useEffect } from 'react';
import useDebounce from '@creditflowresearch/uilib/hooks/useDebounce';
import { GroupBase, OptionsOrGroups } from 'react-select';
import type { AsyncProps } from 'react-select/async';
import { ISelectOption, ISelectCustomProps, IAsyncSelectCustomProps, IAsyncSelectDebounceConfig } from '../interfaces';

const DEFAULT_DEBOUNCE_CONFIG: IAsyncSelectDebounceConfig = { timeout: 500, minInputLength: 1 };

const getBelowMinLengthMessage = (minLength: number): string => (
  minLength === 1 ? 'Start Typing...' : `You need to enter at least ${ minLength } letters`
);

const getDebounceConfig = (
  useDebounceProp: IAsyncSelectCustomProps['useDebounce'],
): IAsyncSelectDebounceConfig | undefined => {
  if (!useDebounceProp) return undefined;
  return useDebounceProp === true ? DEFAULT_DEBOUNCE_CONFIG : useDebounceProp;
};

interface IUseDebouncedLoadOptionsProps<SOption extends ISelectOption, Group extends GroupBase<SOption>> {
  useDebounce: IAsyncSelectCustomProps['useDebounce'],
  loadOptions: AsyncProps<SOption, boolean, Group>['loadOptions'],
  defaultOptions: OptionsOrGroups<SOption, Group> | boolean | undefined,
  noOptionsMessage: ISelectCustomProps['noOptionsMessage'],
}

/**
 * Wraps `loadOptions` with a debounce + minimum-input-length gate driven by the
 * `useDebounce` prop, and generates a matching `noOptionsMessage` for the
 * below-minimum-length state. Mirrors `@dealroadshow/uikit-select`'s AsyncSelect
 * so callers don't have to hand-roll debounce/gating per component.
 */
const useDebouncedLoadOptions = <
  SOption extends ISelectOption = ISelectOption,
  Group extends GroupBase<SOption> = GroupBase<SOption>,
>({
  useDebounce: useDebounceConfig,
  loadOptions,
  defaultOptions,
  noOptionsMessage,
}: IUseDebouncedLoadOptionsProps<SOption, Group>) => {
  const config = getDebounceConfig(useDebounceConfig);
  const { timeout = 500, minInputLength = 1 } = config ?? {};

  // `loadOptions` may either invoke `callback` itself or return a Promise
  const fetchOptions = useCallback((
    inputValue: string,
    callback: (options: OptionsOrGroups<SOption, Group>) => void,
  ) => {
    const result = loadOptions?.(inputValue, callback);
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      (result as Promise<OptionsOrGroups<SOption, Group>>).then(callback, () => callback([]));
    }
  }, [loadOptions]);

  const debouncedFetch = useDebounce(fetchOptions, timeout);

  useEffect(() => () => {
    debouncedFetch.cancel();
  }, [debouncedFetch]);

  const debouncedLoadOptions = useCallback((
    inputValue: string,
    callback: (options: OptionsOrGroups<SOption, Group>) => void,
  ) => {
    if (inputValue.length < minInputLength) {
      debouncedFetch.cancel();
      callback(Array.isArray(defaultOptions) ? defaultOptions : []);
      return;
    }
    debouncedFetch(inputValue, callback);
  }, [debouncedFetch, minInputLength, defaultOptions]);

  return {
    loadOptions: config ? debouncedLoadOptions : loadOptions,
    noOptionsMessage: config
      ? ({ inputValue }: { inputValue: string }) => (
        inputValue.length < minInputLength
          ? config.belowMinLengthMessage ?? getBelowMinLengthMessage(minInputLength)
          : noOptionsMessage?.({ inputValue }) ?? 'No results found'
      )
      : noOptionsMessage,
  };
};

export default useDebouncedLoadOptions;
